import ChartCard from "@ui/Highcharts/ui/Card";
import React from "react";
import { css, cx } from "@linaria/core";
import { spacings } from "@ui/Theme";
import { FullViewportButtonMarker } from "@ui/FullViewportButton";
import { exportWidgetTarget } from "@ui/ExportWidgetButton";
import Amplitude from "./Amplitude";
import Gap from "./Gap";
import NormalizedGap from "./NormalizedGap";
import type { SymptomChartType } from "../../../../../../Symptoms/SymptomChartType";
import type { ActiveAnalysis } from "../../../../../../../../api/useActiveAnalysesStream";
import type { Project } from "../../../../../../../../api/useProjects";
import type { Source } from "../../../../../../../../api/useSources";
import type { SelectedQuantity } from "../../../../../../useSelectedQuantities";
import type { NonEmptyArray } from "diagnostics-lib/array";
import { useQuantityIndex, type QuantityIndex } from "../../../../../../../../api/useQuantities";
import type { Setup } from "../../../../../../../../api/useSetups";
import {
  useMetrologyOverrides,
  type MetrologyOverrides,
} from "../../../../../../../../api/useMetrologyOverrides";
import MeasuresLegend from "../../../../../../Chart/Legend/MeasuresLegend";
import SymptomsLegend from "../../../../../../Chart/Legend/SymptomsLegend";
import ExpectedLegend from "../../../../../../Chart/Legend/ExpectedLegend";
import CommonLegend from "../../../../../../Chart/Legend/CommonLegend";
import { useEventPeriod } from "../../../../../hooks/useEventPeriod";
import type { Event } from "../../../../../../../../api/useEvents";
import { useSettings } from "../../../../../../../../api/useSettings";
import { useCanUserDoOnProject } from "../../../../../../../canUserDo";
import useActivationFilters from "../../../../../../../../api/useActivationFilters";
import { useDisplayInvalidPoints } from "../../../../../../Analysis/ToggleDisplayInvalidPoints";
import { usePublicationFlagDisplay } from "../../../../../../Analysis/TogglePublicationDisplay";
import { useQuantityNoDataIntervals } from "../../../../../../useNoDataIntervals";
import getAmplitudeSeries from "../../../../../../Symptoms/Amplitude/getSeries";
import getGapSeries from "../../../../../../Symptoms/Gap/getSeries";

interface ChartProps {
  chartRef: React.RefObject<Highcharts.Chart>;
  selectedChartOption: SymptomChartType;
  activeAnalyses: ActiveAnalysis[];
  project: Project;
  source: Source;
  timeZone: string;
  series: ReturnType<typeof getSymptomChartSeries>;
  selectedQuantities: NonEmptyArray<SelectedQuantity>;
  event: Event;
}

function getSymptomChartSeries({
  project,
  quantityIndex,
  chartType,
  analyses,
  selectedQuantities,
  canUserWriteProject,
  setups,
  activationFilters,
  publicationFlagDisplayed,
  metrologyOverrides,
  displayInvalidPoints,
}: {
  project: Project;
  quantityIndex: QuantityIndex;
  chartType: SymptomChartType;
  publishedAnalysesVisibled: boolean;
  analyses: ActiveAnalysis[];
  selectedQuantities: NonEmptyArray<SelectedQuantity>;
  canUserWriteProject: boolean;
  setups: Setup[];
  activationFilters: {
    quantityId: string;
    min: number | null;
    max: number | null;
    maxStd: number | null;
  }[];
  publicationFlagDisplayed: boolean;
  metrologyOverrides: MetrologyOverrides;
  displayInvalidPoints: boolean;
}) {
  //action allowed if udpdate, delete permission
  switch (chartType) {
    case "amplitude":
      return getAmplitudeSeries(
        project,
        analyses,
        quantityIndex,
        selectedQuantities[0],
        canUserWriteProject,
        setups,
        activationFilters,
        publicationFlagDisplayed,
        metrologyOverrides,
        displayInvalidPoints,
      );
    case "gap":
      return getGapSeries(
        project,
        analyses,
        [selectedQuantities[0]],
        quantityIndex,
        false,
        setups,
        publicationFlagDisplayed,
        chartType,
        metrologyOverrides,
        displayInvalidPoints,
      );
    case "normalizedGap":
      return getGapSeries(
        project,
        analyses,
        selectedQuantities,
        quantityIndex,
        true,
        setups,
        publicationFlagDisplayed,
        chartType,
        metrologyOverrides,
        displayInvalidPoints,
      );
    default:
      throw new Error(chartType satisfies never);
  }
}

function Chart({
  chartRef,
  series,
  activeAnalyses,
  project,
  source,
  timeZone,
  selectedChartOption,
  selectedQuantities,
  event,
}: ChartProps) {
  if (selectedChartOption === "normalizedGap") {
    return (
      <NormalizedGap
        chartRef={chartRef}
        series={series}
        analyses={activeAnalyses}
        project={project}
        source={source}
        selectedQuantities={selectedQuantities}
        timeZone={timeZone}
        event={event}
      />
    );
  } else if (selectedChartOption === "gap") {
    return (
      <Gap
        chartRef={chartRef}
        series={series}
        analyses={activeAnalyses}
        project={project}
        source={source}
        selectedQuantities={selectedQuantities}
        timeZone={timeZone}
        event={event}
      />
    );
  } else if (selectedChartOption === "amplitude") {
    return (
      <Amplitude
        chartRef={chartRef}
        analyses={activeAnalyses}
        series={series}
        project={project}
        source={source}
        selectedQuantities={selectedQuantities}
        timeZone={timeZone}
        event={event}
      />
    );
  } else {
    throw new Error(selectedChartOption satisfies never);
  }
}

interface Props {
  title: React.JSX.Element;
  subtitle: React.JSX.Element;

  project: Project;
  source: Source;
  activeAnalyses: ActiveAnalysis[];
  setups: Setup[];
  selectedChartOption: SymptomChartType;
  selectedQuantities: NonEmptyArray<SelectedQuantity>;
  event: Event;
}

const SymptomHighCharts = ({
  title,
  subtitle,
  project,
  setups,
  activeAnalyses,
  source,
  selectedChartOption,
  selectedQuantities,
  event,
}: Props) => {
  const settings = useSettings();
  const projectId = project.id;
  const canUserWriteProject = useCanUserDoOnProject("PROJECT_WRITE", project.id);
  const metrologyOverrides = useMetrologyOverrides(project.id);
  const quantityIndex = useQuantityIndex(project.id);
  const activationFilters = useActivationFilters(project.id);

  const [displayInvalidPoints] = useDisplayInvalidPoints({
    scope: project.id,
  });
  const [publicationFlagDisplayed] = usePublicationFlagDisplay({
    scope: project.id,
    defaultValue: settings.DISPLAY_PUBLICATION_MARKERS,
  });

  const [period, _, __, ___, ____, defaultFaultEventPeriod] = useEventPeriod({
    event,
    source,
    project,
    analyses: activeAnalyses,
    boundaries: "active-analyses",
  });

  const chartRef = React.useRef<Highcharts.Chart>(null);

  const series = React.useMemo(
    () =>
      getSymptomChartSeries({
        project,
        quantityIndex,
        chartType: selectedChartOption,
        publishedAnalysesVisibled: false,
        analyses: activeAnalyses,
        selectedQuantities,
        setups,
        canUserWriteProject,
        activationFilters,
        publicationFlagDisplayed,
        metrologyOverrides,
        displayInvalidPoints,
      }),
    [
      project,
      quantityIndex,
      selectedChartOption,
      activeAnalyses,
      selectedQuantities,
      setups,
      canUserWriteProject,
      activationFilters,
      publicationFlagDisplayed,
      metrologyOverrides,
      displayInvalidPoints,
    ],
  );

  const noDataIntervals = useQuantityNoDataIntervals(
    activeAnalyses,
    selectedQuantities,
    selectedChartOption,
    project.id,
  );

  const legend = React.useMemo(
    () => (
      <>
        {selectedChartOption === "amplitude" && (
          <MeasuresLegend
            chartRef={chartRef}
            selectedQuantities={selectedQuantities}
            series={series}
            period={period}
            displayInvalidPoints={displayInvalidPoints}
          />
        )}
        {selectedChartOption === "amplitude" && <ExpectedLegend series={series} period={period} />}
        {(selectedChartOption === "gap" || selectedChartOption === "normalizedGap") && (
          <SymptomsLegend period={period} series={series} selectedQuantities={selectedQuantities} />
        )}
        <CommonLegend
          noDataIntervals={noDataIntervals}
          chartRef={chartRef}
          analyses={activeAnalyses}
          series={series}
          period={period}
          chartType={selectedChartOption}
          projectId={projectId}
          defaultFaultEventPeriod={defaultFaultEventPeriod}
        />
      </>
    ),
    [
      selectedChartOption,
      selectedQuantities,
      series,
      period,
      displayInvalidPoints,
      noDataIntervals,
      activeAnalyses,
      projectId,
      defaultFaultEventPeriod,
    ],
  );

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      legend={legend}
      className={cx(symptomEvolutionChart, FullViewportButtonMarker, exportWidgetTarget)}
    >
      <Chart
        chartRef={chartRef}
        selectedChartOption={selectedChartOption}
        series={series}
        activeAnalyses={activeAnalyses}
        project={project}
        source={source}
        selectedQuantities={selectedQuantities}
        timeZone={project.timezone}
        event={event}
      />
    </ChartCard>
  );
};

export default SymptomHighCharts;

//#region CSS
const symptomEvolutionChart = css`
  row-gap: ${spacings.m};
`;
//#endregion CSS
